import axios from 'axios';

// Determine API URL at runtime - always use current origin in production
const getApiUrl = () => {
  // In development, use environment variable if set
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // In production, always use current origin (same domain)
  return window.location.origin;
};

const API_URL = getApiUrl();

console.log('API URL:', API_URL, 'ENV:', process.env.NODE_ENV);

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: false,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.log('API Request:', config.method?.toUpperCase(), config.url);
  return config;
});

// Handle auth errors and network errors
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API Error:', error.message, error.config?.url);
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Better error message for network errors
    if (error.code === 'ERR_NETWORK' || !error.response) {
      error.message = 'Netzwerkfehler - Bitte prüfen Sie Ihre Verbindung';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Containers
export const containerAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.folder_id) queryParams.append('folder_id', params.folder_id);
    const query = queryParams.toString();
    return api.get(`/containers${query ? `?${query}` : ''}`);
  },
  getOne: (id) => api.get(`/containers/${id}`),
  create: (data) => api.post('/containers', data),
  update: (id, data) => api.put(`/containers/${id}`, data),
  delete: (id) => api.delete(`/containers/${id}`),
  reorder: (orders) => api.put('/containers/reorder', orders),
  getScans: (id) => api.get(`/containers/${id}/scans`),
  getRemarks: (id) => api.get(`/containers/${id}/remarks`),
  addRemark: (id, data) => api.post(`/containers/${id}/remarks`, data),
  maintenance: (id, action) => api.post(`/containers/${id}/maintenance`, { action }),
  getQRCode: (id) => api.get(`/containers/${id}/qrcode`, { responseType: 'blob' }),
};

// Categories
export const categoryAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  init: () => api.post('/categories/init'),
};

// Companies
export const companyAPI = {
  getAll: (includeInactive = false) => api.get(`/companies?include_inactive=${includeInactive}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  toggleActive: (id) => api.post(`/companies/${id}/toggle-active`),
};

// Folders
export const folderAPI = {
  getAll: () => api.get('/folders'),
  create: (data) => api.post('/folders', data),
  update: (id, data) => api.put(`/folders/${id}`, data),
  delete: (id) => api.delete(`/folders/${id}`),
  // File management
  getFiles: (folderId) => api.get(`/folders/${folderId}/files`),
  uploadFile: (folderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/folders/${folderId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadFile: (folderId, fileId) => api.get(`/folders/${folderId}/files/${fileId}/download`, { responseType: 'blob' }),
  deleteFile: (folderId, fileId) => api.delete(`/folders/${folderId}/files/${fileId}`),
};

// Video Folders
export const videoFolderAPI = {
  getAll: () => api.get('/video-folders'),
  create: (data) => api.post('/video-folders', data),
  update: (id, data) => api.put(`/video-folders/${id}`, data),
  delete: (id) => api.delete(`/video-folders/${id}`),
  // Video management
  getVideos: (folderId) => api.get(`/video-folders/${folderId}/videos`),
  uploadVideo: (folderId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/video-folders/${folderId}/videos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
    });
  },
  getStreamUrl: (folderId, videoId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/video-folders/${folderId}/videos/${videoId}/stream?token=${token}`;
  },
  downloadVideo: (folderId, videoId) => api.get(`/video-folders/${folderId}/videos/${videoId}/download`, { responseType: 'blob' }),
  deleteVideo: (folderId, videoId) => api.delete(`/video-folders/${folderId}/videos/${videoId}`),
};

// Checklists
export const checklistAPI = {
  // Templates (Admin)
  getTemplates: () => api.get('/checklists/templates'),
  getTemplate: (id) => api.get(`/checklists/templates/${id}`),
  createTemplate: (data) => api.post('/checklists/templates', data),
  updateTemplate: (id, data) => api.put(`/checklists/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/checklists/templates/${id}`),
  // Submissions (User)
  submitChecklist: (templateId, data) => api.post(`/checklists/templates/${templateId}/submit`, data),
  getSubmissions: (templateId = null) => api.get(`/checklists/submissions${templateId ? `?template_id=${templateId}` : ''}`),
  getSubmission: (id) => api.get(`/checklists/submissions/${id}`),
  // PDF Export
  exportPdf: (id) => api.get(`/checklists/templates/${id}/export-pdf`, { responseType: 'blob' }),
};

// Scans
export const scanAPI = {
  process: (barcode) => api.post('/scan', { barcode }),
  getAll: (limit = 100) => api.get(`/scans?limit=${limit}`),
};

// Stats
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Users (Admin)
export const userAPI = {
  getAll: () => api.get('/users'),
  getOne: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
};

// Export
export const exportAPI = {
  csv: () => api.get('/export/csv', { responseType: 'blob' }),
  excel: () => api.get('/export/excel', { responseType: 'blob' }),
};

// Import
export const importAPI = {
  containers: (data) => api.post('/import/containers', data),
};

// Notes
export const noteAPI = {
  getAll: (filter = null) => api.get(`/notes${filter ? `?filter=${filter}` : ''}`),
  getOne: (id) => api.get(`/notes/${id}`),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.put(`/notes/${id}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
  bulkDelete: (noteIds) => api.post('/notes/bulk-delete', { note_ids: noteIds }),
  toggleImportant: (id) => api.post(`/notes/${id}/toggle-important`),
};

// Rapport Books
export const rapportAPI = {
  // Books
  getBooks: (includeInactive = false) => api.get(`/rapport-books${includeInactive ? '?include_inactive=true' : ''}`),
  getBook: (id) => api.get(`/rapport-books/${id}`),
  createBook: (data) => api.post('/rapport-books', data),
  updateBook: (id, data) => api.put(`/rapport-books/${id}`, data),
  deleteBook: (id) => api.delete(`/rapport-books/${id}`),
  exportPdf: (id) => api.get(`/rapport-books/${id}/export-pdf`, { responseType: 'blob' }),
  // Entries
  getEntries: (bookId) => api.get(`/rapport-books/${bookId}/entries`),
  createEntry: (bookId, data) => api.post(`/rapport-books/${bookId}/entries`, data),
  deleteEntry: (id) => api.delete(`/rapport-entries/${id}`),
};

export default api;
